import { useTicketBusinessStateHistory } from '@/hooks/tickets/useTicketBusinessState';
import { stateLabel } from '@/lib/tickets/businessStateMachine';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, RotateCcw, AlertOctagon, Forward } from 'lucide-react';

interface Props {
  ticketId: string;
}

/**
 * Phase 16 — Timeline of every business-state transition for a ticket.
 * Forward moves render neutral; regressions render amber; voids render red.
 */
export function TicketBusinessStateTimeline({ ticketId }: Props) {
  const { data: history, isLoading } = useTicketBusinessStateHistory(ticketId);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground p-4">Loading history…</div>;
  }
  if (!history?.length) {
    return <div className="text-sm text-muted-foreground p-4">No transitions yet.</div>;
  }

  return (
    <ScrollArea className="h-72 rounded-md border">
      <ol className="divide-y">
        {history.map((row) => {
          const Icon =
            row.transition_kind === 'regression'
              ? RotateCcw
              : row.transition_kind === 'manual_override'
                ? AlertOctagon
                : Forward;
          const tone =
            row.transition_kind === 'regression'
              ? 'text-warning-foreground'
              : row.transition_kind === 'manual_override'
                ? 'text-destructive'
                : 'text-muted-foreground';
          return (
            <li key={row.id} className="px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${tone}`} />
                <span className="font-medium">
                  {row.from_state ? stateLabel(row.from_state) : '—'}
                </span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium">{stateLabel(row.to_state)}</span>
                <Badge variant="outline" className="ml-auto text-[10px] uppercase">
                  {row.transition_kind}
                </Badge>
              </div>
              {row.reason && (
                <div className="text-xs text-muted-foreground mt-0.5 ml-5">
                  {row.reason}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-0.5 ml-5">
                {new Date(row.created_at).toLocaleString()}
                {row.actor_role ? ` · ${row.actor_role}` : ''}
              </div>
            </li>
          );
        })}
      </ol>
    </ScrollArea>
  );
}
